// Bloque «chart»: barras, líneas o dona con los números que ya agrupó el
// servidor (nunca llegan registros). Una serie usa el color del sitio; varias
// series (o las porciones de una dona) usan la paleta fija `--site-series-*`
// con leyenda. Siempre hay una tabla con los datos («Ver datos»): algunos
// colores quedan bajo 3:1 en claro y la tabla es lo que se lee sin la gráfica.

import { useMemo } from "react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Line,
  LineChart,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
  type TooltipContentProps,
} from "recharts";
import { cn } from "@/lib/utils";
import { formatSiteValue, type SiteBlockOf } from "../values";
import { BlockEmpty, BlockTitle } from "./shared";

type ChartData = SiteBlockOf<"chart">;

const HEIGHT = 220;
const SERIES_COLORS = Array.from({ length: 8 }, (_, i) => `var(--site-series-${i + 1})`);
const numberFormat = new Intl.NumberFormat("es", { maximumFractionDigits: 2 });
const compactFormat = new Intl.NumberFormat("es", { notation: "compact", maximumFractionDigits: 1 });
const AXIS_TICK = { fontSize: 11, fill: "var(--muted-foreground)" } as const;

const DATE_RE = /^\d{4}-\d{2}-\d{2}(T|$)/;
/** Una categoría que es una fecha («2026-10-01») se muestra como fecha. */
export const categoryText = (c: string) => (DATE_RE.test(c) ? formatSiteValue(c, "date") : c);
/** En el eje, una fecha corta («1 oct»; con el año si las fechas cruzan de año). */
const tickText = (c: string, sameYear: boolean) => {
  const text = categoryText(c);
  return DATE_RE.test(c) && sameYear ? text.replace(/\s\d{4}$/, "") : text;
};
/** Ancho aproximado de un texto del eje (11 px). */
const textWidth = (s: string) => s.length * 6.5;
/** Ancho de trazado que se supone (celular de 390 px): las etiquetas se acomodan para él. */
const PLOT_WIDTH = 300;
const truncate = (s: string, max: number) => (s.length > max ? `${s.slice(0, max - 1)}…` : s);
const formatValue = (v: number | null | undefined) => (v == null ? "—" : numberFormat.format(v));

/** Color de la serie `i` (una sola serie: el del sitio). */
const colorOf = (i: number, count: number) => (count === 1 ? "var(--primary)" : SERIES_COLORS[i % SERIES_COLORS.length]);

function LegendKey({ kind, color }: { kind: ChartData["kind"]; color: string }) {
  return kind === "line" ? (
    <span className="h-0.5 w-3 shrink-0 rounded-full" style={{ background: color }} aria-hidden />
  ) : (
    <span className="size-2.5 shrink-0 rounded-[2px]" style={{ background: color }} aria-hidden />
  );
}

function ChartLegend({ kind, entries }: { kind: ChartData["kind"]; entries: Array<{ name: string; color: string }> }) {
  return (
    <ul className="flex flex-wrap gap-x-4 gap-y-1.5 text-xs text-muted-foreground" data-testid="site-chart-legend">
      {entries.map((e) => (
        <li key={e.name} className="flex min-w-0 items-center gap-1.5">
          <LegendKey kind={kind} color={e.color} />
          <span className="truncate">{e.name}</span>
        </li>
      ))}
    </ul>
  );
}

/** Valores primero (fuertes), la serie después; una sola lista por categoría. */
function ChartTooltip({
  active,
  payload,
  label,
  kind,
}: Partial<Pick<TooltipContentProps, "active" | "payload" | "label">> & { kind: ChartData["kind"] }) {
  if (!active || !payload?.length) return null;
  const rows = payload.filter((p) => p.value != null);
  if (!rows.length) return null;
  const heading = kind === "donut" ? undefined : categoryText(String(label ?? ""));
  return (
    <div className="min-w-32 rounded-md border bg-popover px-3 py-2 text-xs text-popover-foreground shadow-md">
      {heading && <p className="mb-1 text-muted-foreground">{heading}</p>}
      <ul className="space-y-1">
        {rows.map((p) => {
          const color = (p.payload as { color?: string } | undefined)?.color ?? p.color ?? "var(--primary)";
          return (
            <li key={String(p.dataKey ?? p.name)} className="flex items-center gap-2">
              <span className="h-0.5 w-3 shrink-0 rounded-full" style={{ background: color }} aria-hidden />
              <strong className="font-semibold tabular-nums text-foreground">{formatValue(Number(p.value))}</strong>
              <span className="truncate text-muted-foreground">{kind === "donut" ? categoryText(String(p.name)) : p.name}</span>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

/** Etiqueta del eje x: corta los textos largos; inclinada si son muchas o largas. */
function CategoryTick({
  x,
  y,
  payload,
  angled,
  max,
  sameYear,
}: {
  x?: number;
  y?: number;
  payload?: { value: string };
  angled: boolean;
  max: number;
  sameYear: boolean;
}) {
  const value = String(payload?.value ?? "");
  const text = tickText(value, sameYear);
  return (
    <g transform={`translate(${x ?? 0},${y ?? 0})`}>
      <title>{categoryText(value)}</title>
      <text
        dy={angled ? 4 : 12}
        textAnchor={angled ? "end" : "middle"}
        transform={angled ? "rotate(-35)" : undefined}
        fontSize={AXIS_TICK.fontSize}
        fill={AXIS_TICK.fill}
      >
        {truncate(text, max)}
      </text>
    </g>
  );
}

function ChartTable({ block }: { block: ChartData }) {
  const single = block.series.length === 1;
  return (
    <details className="group text-sm" data-testid="site-chart-data">
      <summary className="cursor-pointer select-none text-sm font-medium text-muted-foreground hover:text-foreground">
        Ver datos
      </summary>
      <div className="mt-2 overflow-x-auto rounded-md border">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b bg-muted/60">
              <th scope="col" className="px-3 py-2 text-left font-medium text-muted-foreground">
                <span className="sr-only">Categoría</span>
              </th>
              {block.series.map((s) => (
                <th key={s.name} scope="col" className="px-3 py-2 text-right font-medium text-muted-foreground">
                  {single ? block.valueLabel : s.name}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {block.categories.map((c, i) => (
              <tr key={c} className="border-b last:border-0">
                <th scope="row" className="px-3 py-2 text-left font-normal text-foreground">
                  {categoryText(c)}
                </th>
                {block.series.map((s) => (
                  <td key={s.name} className="px-3 py-2 text-right tabular-nums text-foreground">
                    {formatValue(s.values[i])}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </details>
  );
}

function Plot({ block }: { block: ChartData }) {
  const { kind, categories, series } = block;
  const rows = useMemo(
    () => categories.map((category, i) => Object.fromEntries([["category", category], ...series.map((s, si) => [`s${si}`, s.values[i]])])),
    [categories, series],
  );
  const integers = series.every((s) => s.values.every((v) => v == null || Number.isInteger(v)));

  if (kind === "donut") {
    const values = series[0]?.values ?? [];
    const slices = categories
      .map((name, i) => ({ name, value: values[i] ?? 0, color: colorOf(i, 2) }))
      .filter((s) => s.value > 0);
    return (
      <ResponsiveContainer width="100%" height={HEIGHT}>
        <PieChart>
          <Pie
            data={slices}
            dataKey="value"
            nameKey="name"
            innerRadius="60%"
            outerRadius="95%"
            // El separador va entre porciones: con una sola quedaba una raya.
            stroke={slices.length > 1 ? "var(--card)" : "none"}
            strokeWidth={2}
            isAnimationActive={false}
          >
            {slices.map((s) => (
              <Cell key={s.name} fill={s.color} />
            ))}
          </Pie>
          <Tooltip content={(p) => <ChartTooltip active={p.active} payload={p.payload} label={p.label} kind="donut" />} />
        </PieChart>
      </ResponsiveContainer>
    );
  }

  const years = new Set(categories.filter((c) => DATE_RE.test(c)).map((c) => c.slice(0, 4)));
  const sameYear = years.size <= 1;
  const longest = Math.max(0, ...categories.map((c) => Math.min(tickText(c, sameYear).length, 14)));
  // Rectas si caben todas en el celular; si no, inclinadas (y cortas). Si ni así caben, se saltan algunas.
  const slot = PLOT_WIDTH / Math.max(1, categories.length);
  const angled = textWidth("x".repeat(longest)) + 8 > slot;
  const every = !angled || slot >= 14;
  const max = angled ? 12 : 14;
  const xAxis = (
    <XAxis
      dataKey="category"
      tickLine={false}
      axisLine={{ stroke: "var(--border)" }}
      interval={every ? 0 : "preserveStartEnd"}
      // Las líneas llegan al borde: aire para la primera y la última etiqueta.
      padding={kind === "line" ? { left: 14, right: 14 } : undefined}
      minTickGap={2}
      height={angled ? 60 : 24}
      tick={<CategoryTick angled={angled} max={max} sameYear={sameYear} />}
    />
  );
  // Ancho del eje y según su número más largo («1,2 mil»).
  const top = Math.max(0, ...series.flatMap((s) => s.values.map((v) => Math.abs(v ?? 0))));
  // (+2 caracteres: las marcas intermedias pueden llevar decimales, «2,3 mil».)
  const yWidth = Math.max(28, Math.ceil(textWidth(`${compactFormat.format(top)},0`)) + 10);
  const yAxis = (
    <YAxis
      width={yWidth}
      tick={AXIS_TICK}
      tickLine={false}
      axisLine={false}
      allowDecimals={!integers}
      // Barras desde 0 (la altura es el valor); las líneas, al rango de los
      // datos para que se vea el cambio (notas de 70 a 95 no quedan pegadas).
      domain={kind === "line" ? ["auto", "auto"] : [0, "auto"]}
      tickFormatter={(v: number) => compactFormat.format(v)}
    />
  );
  const grid = <CartesianGrid vertical={false} stroke="var(--border)" strokeWidth={1} />;
  // Inclinadas, la primera etiqueta sale hacia la izquierda: margen para que no se corte.
  const margin = { top: 8, right: 8, left: angled ? 16 : 0, bottom: 0 };
  const tooltip = (
    <Tooltip
      content={(p) => <ChartTooltip active={p.active} payload={p.payload} label={p.label} kind={kind} />}
      cursor={kind === "bar" ? { fill: "var(--muted)", opacity: 0.6 } : { stroke: "var(--border)" }}
    />
  );

  if (kind === "line") {
    return (
      <ResponsiveContainer width="100%" height={HEIGHT}>
        <LineChart data={rows} margin={margin}>
          {grid}
          {xAxis}
          {yAxis}
          {tooltip}
          {series.map((s, i) => {
            const color = colorOf(i, series.length);
            return (
              <Line
                key={s.name}
                type="linear"
                dataKey={`s${i}`}
                name={s.name}
                stroke={color}
                strokeWidth={2}
                strokeLinecap="round"
                strokeLinejoin="round"
                dot={{ r: 4, strokeWidth: 2, stroke: "var(--card)", fill: color }}
                activeDot={{ r: 5, strokeWidth: 2, stroke: "var(--card)", fill: color }}
                connectNulls={false}
                isAnimationActive={false}
              />
            );
          })}
        </LineChart>
      </ResponsiveContainer>
    );
  }

  return (
    <ResponsiveContainer width="100%" height={HEIGHT}>
      <BarChart data={rows} margin={margin} barGap={2} barCategoryGap="20%">
        {grid}
        {xAxis}
        {yAxis}
        {tooltip}
        {series.map((s, i) => (
          <Bar
            key={s.name}
            dataKey={`s${i}`}
            name={s.name}
            fill={colorOf(i, series.length)}
            maxBarSize={24}
            radius={[4, 4, 0, 0]}
            isAnimationActive={false}
          />
        ))}
      </BarChart>
    </ResponsiveContainer>
  );
}

export function ChartBlock({ block }: { block: ChartData }) {
  if (!block.categories.length) {
    return (
      <section className="space-y-3">
        {block.title && <BlockTitle>{block.title}</BlockTitle>}
        <BlockEmpty>Todavía no hay nada para mostrar.</BlockEmpty>
      </section>
    );
  }
  const legend =
    block.kind === "donut"
      ? block.categories.map((c, i) => ({ name: categoryText(c), color: colorOf(i, 2) }))
      : block.series.length >= 2
        ? block.series.map((s, i) => ({ name: s.name, color: colorOf(i, block.series.length) }))
        : [];
  const label = block.title ?? block.valueLabel;
  return (
    <section className="space-y-3" aria-label={label} data-testid={`site-chart-${block.key}`}>
      {block.title && <BlockTitle>{block.title}</BlockTitle>}
      <div className="space-y-3 rounded-lg border bg-card p-4 text-card-foreground">
        {legend.length > 0 && <ChartLegend kind={block.kind} entries={legend} />}
        <div role="img" aria-label={label} className={cn("w-full", block.kind === "donut" && "mx-auto max-w-80")} style={{ height: HEIGHT }}>
          <Plot block={block} />
        </div>
        <ChartTable block={block} />
      </div>
    </section>
  );
}
